import * as Dialog from '@radix-ui/react-dialog'
import Button from '@ui/Button'
import { IconX } from '@ui/icons'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export type SheetAction = {
  labelKey?: string
  labelOpts?: Record<string, string | number>
  onClick: () => void
  loading?: boolean
  disabled?: boolean
  destructive?: boolean
}

type Props = {
  open: boolean
  onClose: () => void
  title: string
  eyebrow?: string
  children: ReactNode
  primary?: SheetAction
  // Defaults to a cancel action when only `primary` is provided.
  secondary?: SheetAction
  // Overrides primary/secondary when set — only for non-standard arrangements.
  footer?: ReactNode
}

const Sheet = ({
  open,
  onClose,
  title,
  eyebrow,
  children,
  primary,
  secondary,
  footer,
}: Props) => {
  const { t } = useTranslation()

  const renderActionFooter = (): ReactNode => {
    if (!primary) return null
    const sec: SheetAction = secondary ?? {
      labelKey: 'common.cancel',
      onClick: onClose,
    }
    return (
      <>
        <Button
          variant="secondary"
          fullWidth
          onClick={sec.onClick}
          disabled={sec.loading || sec.disabled}
        >
          {t(sec.labelKey ?? 'common.cancel')}
        </Button>
        <Button
          variant={primary.destructive ? 'destructive' : 'primary'}
          fullWidth
          onClick={primary.onClick}
          disabled={primary.loading || primary.disabled}
        >
          {t(
            primary.loading
              ? 'common.saving'
              : primary.labelKey ?? 'common.save',
            primary.labelOpts
          )}
        </Button>
      </>
    )
  }

  const resolvedFooter = footer ?? renderActionFooter()

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[100] bg-green-900/50"
          style={{
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          className="
            fixed z-[101] bg-paper flex flex-col
            inset-x-0 bottom-0 rounded-t-xl max-h-[92dvh]
            border-t border-ink-100
            sm:inset-auto sm:bottom-auto sm:top-1/2 sm:left-1/2
            sm:-translate-x-1/2 sm:-translate-y-1/2
            sm:rounded-xl sm:rounded-b-xl sm:w-[520px] sm:max-w-[calc(100vw-32px)]
            sm:max-h-[calc(100vh-64px)]
            sm:border sm:border-ink-200 sm:ring-1 sm:ring-black/5
            sm:overflow-hidden
            focus:outline-none
          "
          style={{
            boxShadow:
              '0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 24px 60px -20px rgba(14, 20, 16, 0.45), 0 8px 20px -8px rgba(14, 20, 16, 0.18)',
          }}
        >
          <header className="flex items-start justify-between gap-2 px-5 pt-5 pb-3">
            <div className="flex flex-col gap-0.5 min-w-0">
              {eyebrow && (
                <span className="text-[11px] font-bold uppercase tracking-caps text-ink-500">
                  {eyebrow}
                </span>
              )}
              <Dialog.Title className="font-display font-bold text-xl tracking-tight text-ink-900 truncate">
                {title}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label={t('common.close')}
                className="w-9 h-9 grid place-items-center rounded-lg text-ink-700 hover:bg-ink-100/60 flex-shrink-0"
              >
                <IconX size={18} />
              </button>
            </Dialog.Close>
          </header>
          <div className="flex-1 overflow-y-auto px-5 pb-5">{children}</div>
          {resolvedFooter && (
            <footer
              className="px-5 pt-3 border-t border-ink-100 bg-paper flex items-center gap-2"
              style={{
                paddingBottom:
                  'max(24px, calc(env(safe-area-inset-bottom) + 12px))',
              }}
            >
              {resolvedFooter}
            </footer>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default Sheet
